import { getApplicativeMonoid } from './Applicative';
import { apFirst as apFirst_, apS as apS_, apSecond as apSecond_, getApplySemigroup as getApplySemigroup_ } from './Apply';
import { bind as bind_, chainFirst as chainFirst_ } from './Chain';
import { tailRec } from './ChainRec';
import { chainOptionK as chainOptionK_, filterOrElse as filterOrElse_, fromOption as fromOption_, fromOptionK as fromOptionK_, fromPredicate as fromPredicate_ } from './FromEither';
import { flow, identity, pipe } from './function';
import { bindTo as bindTo_, flap as flap_ } from './Functor';
import * as _ from './internal';
import { separated } from './Separated';
import { wiltDefault, witherDefault } from './Witherable'; // -------------------------------------------------------------------------------------
// constructors
// -------------------------------------------------------------------------------------

/**
 * Constructs a new `Either` holding a `Left` value. This usually represents a failure, due to the right-bias of this
 * structure.
 *
 * @category constructors
 * @since 2.0.0
 */

export var left = _.left;
/**
 * Constructs a new `Either` holding a `Right` value. This usually represents a successful value due to the right bias
 * of this structure.
 *
 * @category constructors
 * @since 2.0.0
 */

export var right = _.right; // -------------------------------------------------------------------------------------
// non-pipeables
// -------------------------------------------------------------------------------------

var _map = function _map(fa, f) {
  return pipe(fa, map(f));
};

var _ap = function _ap(fab, fa) {
  return pipe(fab, ap(fa));
};
/* istanbul ignore next */


var _chain = function _chain(ma, f) {
  return pipe(ma, chain(f));
};
/* istanbul ignore next */


var _reduce = function _reduce(fa, b, f) {
  return pipe(fa, reduce(b, f));
};
/* istanbul ignore next */


var _foldMap = function _foldMap(M) {
  return function (fa, f) {
    var foldMapM = foldMap(M);
    return pipe(fa, foldMapM(f));
  };
};
/* istanbul ignore next */


var _reduceRight = function _reduceRight(fa, b, f) {
  return pipe(fa, reduceRight(b, f));
};

var _traverse = function _traverse(F) {
  var traverseF = traverse(F);
  return function (ta, f) {
    return pipe(ta, traverseF(f));
  };
};

var _bimap = function _bimap(fa, f, g) {
  return pipe(fa, bimap(f, g));
};

var _mapLeft = function _mapLeft(fa, f) {
  return pipe(fa, mapLeft(f));
};
/* istanbul ignore next */


var _alt = function _alt(fa, that) {
  return pipe(fa, alt(that));
};
/* istanbul ignore next */


var _extend = function _extend(wa, f) {
  return pipe(wa, extend(f));
};

var _chainRec = function _chainRec(a, f) {
  return tailRec(f(a), function (e) {
    return isLeft(e) ? right(left(e.left)) : isLeft(e.right) ? left(f(e.right.left)) : right(right(e.right.right));
  });
}; // -------------------------------------------------------------------------------------
// instances
// -------------------------------------------------------------------------------------

/**
 * @category instances
 * @since 2.0.0
 */


export var URI = 'Either';
/**
 * @category instances
 * @since 2.0.0
 */

export var getShow = function getShow(SE, SA) {
  return {
    show: function show(ma) {
      return isLeft(ma) ? "left(" + SE.show(ma.left) + ")" : "right(" + SA.show(ma.right) + ")";
    }
  };
};
/**
 * @category instances
 * @since 2.0.0
 */

export var getEq = function getEq(EL, EA) {
  return {
    equals: function equals(x, y) {
      return x === y || (isLeft(x) ? isLeft(y) && EL.equals(x.left, y.left) : isRight(y) && EA.equals(x.right, y.right));
    }
  };
};
/**
 * Semigroup returning the left-most non-`Left` value. If both operands are `Right`s then the inner values are
 * concatenated using the provided `Semigroup`
 *
 * @example
 * import { getSemigroup, left, right } from 'fp-ts/Either'
 * import { SemigroupSum } from 'fp-ts/number'
 *
 * const S = getSemigroup<string, number>(SemigroupSum)
 * assert.deepStrictEqual(S.concat(left('a'), left('b')), left('a'))
 * assert.deepStrictEqual(S.concat(left('a'), right(2)), right(2))
 * assert.deepStrictEqual(S.concat(right(1), left('b')), right(1))
 * assert.deepStrictEqual(S.concat(right(1), right(2)), right(3))
 *
 * @category instances
 * @since 2.0.0
 */

export var getSemigroup = function getSemigroup(S) {
  return {
    concat: function concat(x, y) {
      return isLeft(y) ? x : isLeft(x) ? y : right(S.concat(x.right, y.right));
    }
  };
};
/**
 * Builds a `Compactable` instance for `Either` given `Monoid` for the left side.
 *
 * @category instances
 * @since 2.10.0
 */

export var getCompactable = function getCompactable(M) {
  var empty = left(M.empty);
  return {
    URI: URI,
    _E: undefined,
    compact: function compact(ma) {
      return isLeft(ma) ? ma : ma.right._tag === 'None' ? empty : right(ma.right.value);
    },
    separate: function separate(ma) {
      return isLeft(ma) ? separated(ma, ma) : isLeft(ma.right) ? separated(right(ma.right.left), empty) : separated(empty, right(ma.right.right));
    }
  };
};
/**
 * Builds a `Filterable` instance for `Either` given `Monoid` for the left side
 *
 * @category instances
 * @since 2.10.0
 */

export var getFilterable = function getFilterable(M) {
  var empty = left(M.empty);

  var _a = getCompactable(M),
      compact = _a.compact,
      separate = _a.separate;

  var filter = function filter(ma, predicate) {
    return isLeft(ma) ? ma : predicate(ma.right) ? ma : empty;
  };

  var partition = function partition(ma, p) {
    return isLeft(ma) ? separated(ma, ma) : p(ma.right) ? separated(empty, right(ma.right)) : separated(right(ma.right), empty);
  };

  return {
    URI: URI,
    _E: undefined,
    map: _map,
    compact: compact,
    separate: separate,
    filter: filter,
    filterMap: function filterMap(ma, f) {
      if (isLeft(ma)) {
        return ma;
      }

      var ob = f(ma.right);
      return ob._tag === 'None' ? empty : right(ob.value);
    },
    partition: partition,
    partitionMap: function partitionMap(ma, f) {
      if (isLeft(ma)) {
        return separated(ma, ma);
      }

      var e = f(ma.right);
      return isLeft(e) ? separated(right(e.left), empty) : separated(empty, right(e.right));
    }
  };
};
/**
 * Builds `Witherable` instance for `Either` given `Monoid` for the left side
 *
 * @category instances
 * @since 2.0.0
 */

export var getWitherable = function getWitherable(M) {
  var F_ = getFilterable(M);
  var C = getCompactable(M);
  return {
    URI: URI,
    _E: undefined,
    map: _map,
    compact: F_.compact,
    separate: F_.separate,
    filter: F_.filter,
    filterMap: F_.filterMap,
    partition: F_.partition,
    partitionMap: F_.partitionMap,
    traverse: _traverse,
    sequence: sequence,
    reduce: _reduce,
    foldMap: _foldMap,
    reduceRight: _reduceRight,
    wither: witherDefault(Traversable, C),
    wilt: wiltDefault(Traversable, C)
  };
};
/**
 * @category instances
 * @since 2.7.0
 */

export var getApplicativeValidation = function getApplicativeValidation(SE) {
  return {
    URI: URI,
    _E: undefined,
    map: _map,
    ap: function ap(fab, fa) {
      return isLeft(fab) ? isLeft(fa) ? left(SE.concat(fab.left, fa.left)) : fab : isLeft(fa) ? fa : right(fab.right(fa.right));
    },
    of: of
  };
};
/**
 * @category instances
 * @since 2.7.0
 */

export var getAltValidation = function getAltValidation(SE) {
  return {
    URI: URI,
    _E: undefined,
    map: _map,
    alt: function alt(me, that) {
      if (isRight(me)) {
        return me;
      }

      var ea = that();
      return isLeft(ea) ? left(SE.concat(me.left, ea.left)) : ea;
    }
  };
};
/**
 * @category instance operations
 * @since 2.0.0
 */

export var map = function map(f) {
  return function (fa) {
    return isLeft(fa) ? fa : right(f(fa.right));
  };
};
/**
 * @category instances
 * @since 2.7.0
 */

export var Functor = {
  URI: URI,
  map: _map
};
/**
 * @category instance operations
 * @since 2.7.0
 */

export var of = right;
/**
 * @category instances
 * @since 2.10.0
 */

export var Pointed = {
  URI: URI,
  of: of
};
/**
 * Less strict version of [`ap`](#ap).
 *
 * @category instance operations
 * @since 2.8.0
 */

export var apW = function apW(fa) {
  return function (fab) {
    return isLeft(fab) ? fab : isLeft(fa) ? fa : right(fab.right(fa.right));
  };
};
/**
 * Apply a function to an argument under a type constructor.
 *
 * @category instance operations
 * @since 2.0.0
 */

export var ap = apW;
/**
 * @category instances
 * @since 2.10.0
 */

export var Apply = {
  URI: URI,
  map: _map,
  ap: _ap
};
/**
 * @category instances
 * @since 2.7.0
 */

export var Applicative = {
  URI: URI,
  map: _map,
  ap: _ap,
  of: of
};
/**
 * Less strict version of [`chain`](#chain).
 *
 * @category instance operations
 * @since 2.6.0
 */

export var chainW = function chainW(f) {
  return function (ma) {
    return isLeft(ma) ? ma : f(ma.right);
  };
};
/**
 * Composes computations in sequence, using the return value of one computation to determine the next computation.
 *
 * @category instance operations
 * @since 2.0.0
 */

export var chain = chainW;
/**
 * @category instances
 * @since 2.10.0
 */

export var Chain = {
  URI: URI,
  map: _map,
  ap: _ap,
  chain: _chain
};
/**
 * @category instances
 * @since 2.7.0
 */

export var Monad = {
  URI: URI,
  map: _map,
  ap: _ap,
  of: of,
  chain: _chain
};
/**
 * Left-associative fold of a structure.
 *
 * @example
 * import { pipe } from 'fp-ts/function'
 * import * as E from 'fp-ts/Either'
 *
 * const startWith = 'prefix'
 * const concat = (a: string, b: string) => `${a}:${b}`
 *
 * assert.deepStrictEqual(
 *   pipe(E.right('a'), E.reduce(startWith, concat)),
 *   'prefix:a'
 * )
 *
 * assert.deepStrictEqual(
 *   pipe(E.left('e'), E.reduce(startWith, concat)),
 *   'prefix'
 * )
 *
 * @category instance operations
 * @since 2.0.0
 */

export var reduce = function reduce(b, f) {
  return function (fa) {
    return isLeft(fa) ? b : f(b, fa.right);
  };
};
/**
 * Map each element of the structure to a monoid, and combine the results.
 *
 * @example
 * import { pipe } from 'fp-ts/function'
 * import * as E from 'fp-ts/Either'
 * import * as S from 'fp-ts/string'
 *
 * const yell = (a: string) => `${a}!`
 *
 * assert.deepStrictEqual(
 *   pipe(E.right('a'), E.foldMap(S.Monoid)(yell)),
 *   'a!'
 * )
 *
 * assert.deepStrictEqual(
 *   pipe(E.left('e'), E.foldMap(S.Monoid)(yell)),
 *   S.Monoid.empty
 * )
 *
 * @category instance operations
 * @since 2.0.0
 */

export var foldMap = function foldMap(M) {
  return function (f) {
    return function (fa) {
      return isLeft(fa) ? M.empty : f(fa.right);
    };
  };
};
/**
 * Right-associative fold of a structure.
 *
 * @example
 * import { pipe } from 'fp-ts/function'
 * import * as E from 'fp-ts/Either'
 *
 * const startWith = 'postfix'
 * const concat = (a: string, b: string) => `${a}:${b}`
 *
 * assert.deepStrictEqual(
 *   pipe(E.right('a'), E.reduceRight(startWith, concat)),
 *   'a:postfix'
 * )
 *
 * assert.deepStrictEqual(
 *   pipe(E.left('e'), E.reduceRight(startWith, concat)),
 *   'postfix'
 * )
 *
 * @category instance operations
 * @since 2.0.0
 */

export var reduceRight = function reduceRight(b, f) {
  return function (fa) {
    return isLeft(fa) ? b : f(fa.right, b);
  };
};
/**
 * @category instances
 * @since 2.7.0
 */

export var Foldable = {
  URI: URI,
  reduce: _reduce,
  foldMap: _foldMap,
  reduceRight: _reduceRight
};
/**
 * Map each element of a structure to an action, evaluate these actions from left to right, and collect the results.
 *
 * @example
 * import { pipe } from 'fp-ts/function'
 * import * as RA from 'fp-ts/ReadonlyArray'
 * import * as E from 'fp-ts/Either'
 * import * as O from 'fp-ts/Option'
 *
 * assert.deepStrictEqual(
 *   pipe(E.right(['a']), E.traverse(O.Applicative)(RA.head)),
 *   O.some(E.right('a'))
 *  )
 *
 * assert.deepStrictEqual(
 *   pipe(E.right([]), E.traverse(O.Applicative)(RA.head)),
 *   O.none
 * )
 *
 * @category instance operations
 * @since 2.6.3
 */

export var traverse = function traverse(F) {
  return function (f) {
    return function (ta) {
      return isLeft(ta) ? F.of(left(ta.left)) : F.map(f(ta.right), right);
    };
  };
};
/**
 * Evaluate each monadic action in the structure from left to right, and collect the results.
 *
 * @example
 * import { pipe } from 'fp-ts/function'
 * import * as E from 'fp-ts/Either'
 * import * as O from 'fp-ts/Option'
 *
 * assert.deepStrictEqual(
 *   pipe(E.right(O.some('a')), E.sequence(O.Applicative)),
 *   O.some(E.right('a'))
 *  )
 *
 * assert.deepStrictEqual(
 *   pipe(E.right(O.none), E.sequence(O.Applicative)),
 *   O.none
 * )
 *
 * @category instance operations
 * @since 2.6.3
 */

export var sequence = function sequence(F) {
  return function (ma) {
    return isLeft(ma) ? F.of(left(ma.left)) : F.map(ma.right, right);
  };
};
/**
 * @category instances
 * @since 2.7.0
 */

export var Traversable = {
  URI: URI,
  map: _map,
  reduce: _reduce,
  foldMap: _foldMap,
  reduceRight: _reduceRight,
  traverse: _traverse,
  sequence: sequence
};
/**
 * Map a pair of functions over the two type arguments of the bifunctor.
 *
 * @category instance operations
 * @since 2.0.0
 */

export var bimap = function bimap(f, g) {
  return function (fa) {
    return isLeft(fa) ? left(f(fa.left)) : right(g(fa.right));
  };
};
/**
 * Map a function over the first type argument of a bifunctor.
 *
 * @category instance operations
 * @since 2.0.0
 */

export var mapLeft = function mapLeft(f) {
  return function (fa) {
    return isLeft(fa) ? left(f(fa.left)) : fa;
  };
};
/**
 * @category instances
 * @since 2.7.0
 */

export var Bifunctor = {
  URI: URI,
  bimap: _bimap,
  mapLeft: _mapLeft
};
/**
 * Less strict version of [`alt`](#alt).
 *
 * @category instance operations
 * @since 2.9.0
 */

export var altW = function altW(that) {
  return function (fa) {
    return isLeft(fa) ? that() : fa;
  };
};
/**
 * Identifies an associative operation on a type constructor. It is similar to `Semigroup`, except that it applies to
 * types of kind `* -> *`.
 *
 * @category instance operations
 * @since 2.0.0
 */

export var alt = altW;
/**
 * @category instances
 * @since 2.7.0
 */

export var Alt = {
  URI: URI,
  map: _map,
  alt: _alt
};
/**
 * @category instance operations
 * @since 2.0.0
 */

export var extend = function extend(f) {
  return function (wa) {
    return isLeft(wa) ? wa : right(f(wa));
  };
};
/**
 * @category instances
 * @since 2.7.0
 */

export var Extend = {
  URI: URI,
  map: _map,
  extend: _extend
};
/**
 * @category instances
 * @since 2.7.0
 */

export var ChainRec = {
  URI: URI,
  map: _map,
  ap: _ap,
  chain: _chain,
  chainRec: _chainRec
};
/**
 * @category instance operations
 * @since 2.6.3
 */

export var throwError = left;
/**
 * @category instances
 * @since 2.7.0
 */

export var MonadThrow = {
  URI: URI,
  map: _map,
  ap: _ap,
  of: of,
  chain: _chain,
  throwError: throwError
};
/**
 * @category instances
 * @since 2.10.0
 */

export var FromEither = {
  URI: URI,
  fromEither: identity
};
/**
 * @example
 * import { fromPredicate, left, right } from 'fp-ts/Either'
 * import { pipe } from 'fp-ts/function'
 *
 * assert.deepStrictEqual(
 *   pipe(
 *     1,
 *     fromPredicate(
 *       (n) => n > 0,
 *       () => 'error'
 *     )
 *   ),
 *   right(1)
 * )
 * assert.deepStrictEqual(
 *   pipe(
 *     -1,
 *     fromPredicate(
 *       (n) => n > 0,
 *       () => 'error'
 *     )
 *   ),
 *   left('error')
 * )
 *
 * @category constructors
 * @since 2.0.0
 */

export var fromPredicate = /*#__PURE__*/fromPredicate_(FromEither); // -------------------------------------------------------------------------------------
// natural transformations
// -------------------------------------------------------------------------------------

/**
 * @example
 * import * as E from 'fp-ts/Either'
 * import { pipe } from 'fp-ts/function'
 * import * as O from 'fp-ts/Option'
 *
 * assert.deepStrictEqual(
 *   pipe(
 *     O.some(1),
 *     E.fromOption(() => 'error')
 *   ),
 *   E.right(1)
 * )
 * assert.deepStrictEqual(
 *   pipe(
 *     O.none,
 *     E.fromOption(() => 'error')
 *   ),
 *   E.left('error')
 * )
 *
 * @category natural transformations
 * @since 2.0.0
 */

export var fromOption = /*#__PURE__*/fromOption_(FromEither); // -------------------------------------------------------------------------------------
// refinements
// -------------------------------------------------------------------------------------

/**
 * Returns `true` if the either is an instance of `Left`, `false` otherwise.
 *
 * @category refinements
 * @since 2.0.0
 */

export var isLeft = _.isLeft;
/**
 * Returns `true` if the either is an instance of `Right`, `false` otherwise.
 *
 * @category refinements
 * @since 2.0.0
 */

export var isRight = _.isRight; // -------------------------------------------------------------------------------------
// destructors
// -------------------------------------------------------------------------------------

/**
 * Less strict version of [`match`](#match).
 *
 * @category destructors
 * @since 2.10.0
 */

export var matchW = function matchW(onLeft, onRight) {
  return function (ma) {
    return isLeft(ma) ? onLeft(ma.left) : onRight(ma.right);
  };
};
/**
 * Alias of [`matchW`](#matchw).
 *
 * @category destructors
 * @since 2.10.0
 */

export var foldW = matchW;
/**
 * Takes two functions and an `Either` value, if the value is a `Left` the inner value is applied to the first function,
 * if the value is a `Right` the inner value is applied to the second function.
 *
 * @example
 * import { match, left, right } from 'fp-ts/Either'
 * import { pipe } from 'fp-ts/function'
 *
 * function onLeft(errors: Array<string>): string {
 *   return `Errors: ${errors.join(', ')}`
 * }
 *
 * function onRight(value: number): string {
 *   return `Ok: ${value}`
 * }
 *
 * assert.strictEqual(
 *   pipe(
 *     right(1),
 *     match(onLeft, onRight)
 *   ),
 *   'Ok: 1'
 * )
 * assert.strictEqual(
 *   pipe(
 *     left(['error 1', 'error 2']),
 *     match(onLeft, onRight)
 *   ),
 *   'Errors: error 1, error 2'
 * )
 *
 * @category destructors
 * @since 2.10.0
 */

export var match = matchW;
/**
 * Alias of [`match`](#match).
 *
 * @category destructors
 * @since 2.0.0
 */

export var fold = match;
/**
 * Less strict version of [`getOrElse`](#getorelse).
 *
 * @category destructors
 * @since 2.6.0
 */

export var getOrElseW = function getOrElseW(onLeft) {
  return function (ma) {
    return isLeft(ma) ? onLeft(ma.left) : ma.right;
  };
};
/**
 * Returns the wrapped value if it's a `Right` or a default value if is a `Left`.
 *
 * @example
 * import { getOrElse, left, right } from 'fp-ts/Either'
 * import { pipe } from 'fp-ts/function'
 *
 * assert.deepStrictEqual(
 *   pipe(
 *     right(1),
 *     getOrElse(() => 0)
 *   ),
 *   1
 * )
 * assert.deepStrictEqual(
 *   pipe(
 *     left('error'),
 *     getOrElse(() => 0)
 *   ),
 *   0
 * )
 *
 * @category destructors
 * @since 2.0.0
 */

export var getOrElse = getOrElseW; // -------------------------------------------------------------------------------------
// combinators
// -------------------------------------------------------------------------------------

/**
 * Derivable from `Functor`.
 *
 * @category combinators
 * @since 2.10.0
 */

export var flap = /*#__PURE__*/flap_(Functor);
/**
 * Combine two effectful actions, keeping only the result of the first.
 *
 * Derivable from `Apply`.
 *
 * @category combinators
 * @since 2.0.0
 */

export var apFirst = /*#__PURE__*/apFirst_(Apply);
/**
 * Combine two effectful actions, keeping only the result of the second.
 *
 * Derivable from `Apply`.
 *
 * @category combinators
 * @since 2.0.0
 */

export var apSecond = /*#__PURE__*/apSecond_(Apply);
/**
 * Composes computations in sequence, using the return value of one computation to determine the next computation and
 * keeping only the result of the first.
 *
 * Derivable from `Chain`.
 *
 * @category combinators
 * @since 2.0.0
 */

export var chainFirst = /*#__PURE__*/chainFirst_(Chain);
/**
 * Less strict version of [`chainFirst`](#chainfirst)
 *
 * Derivable from `Chain`.
 *
 * @category combinators
 * @since 2.8.0
 */

export var chainFirstW = chainFirst;
/**
 * Less strict version of [`flatten`](#flatten).
 *
 * @category combinators
 * @since 2.11.0
 */

export var flattenW = /*#__PURE__*/chainW(identity);
/**
 * The `flatten` function is the conventional monad join operator. It is used to remove one level of monadic structure, projecting its bound argument into the outer level.
 *
 * Derivable from `Chain`.
 *
 * @example
 * import * as E from 'fp-ts/Either'
 *
 * assert.deepStrictEqual(E.flatten(E.right(E.right('a'))), E.right('a'))
 * assert.deepStrictEqual(E.flatten(E.right(E.left('e'))), E.left('e'))
 * assert.deepStrictEqual(E.flatten(E.left('e')), E.left('e'))
 *
 * @category combinators
 * @since 2.0.0
 */

export var flatten = flattenW;
/**
 * Derivable from `Extend`.
 *
 * @category combinators
 * @since 2.0.0
 */

export var duplicate = /*#__PURE__*/extend(identity);
/**
 * @category combinators
 * @since 2.10.0
 */

export var fromOptionK = /*#__PURE__*/fromOptionK_(FromEither);
/**
 * @category combinators
 * @since 2.11.0
 */

export var chainOptionK = /*#__PURE__*/chainOptionK_(FromEither, Chain);
/**
 * @example
 * import * as E from 'fp-ts/Either'
 * import { pipe } from 'fp-ts/function'
 *
 * assert.deepStrictEqual(
 *   pipe(
 *     E.right(1),
 *     E.filterOrElse(
 *       (n) => n > 0,
 *       () => 'error'
 *     )
 *   ),
 *   E.right(1)
 * )
 * assert.deepStrictEqual(
 *   pipe(
 *     E.right(-1),
 *     E.filterOrElse(
 *       (n) => n > 0,
 *       () => 'error'
 *     )
 *   ),
 *   E.left('error')
 * )
 * assert.deepStrictEqual(
 *   pipe(
 *     E.left('a'),
 *     E.filterOrElse(
 *       (n) => n > 0,
 *       () => 'error'
 *     )
 *   ),
 *   E.left('a')
 * )
 *
 * @category combinators
 * @since 2.0.0
 */

export var filterOrElse = /*#__PURE__*/filterOrElse_(FromEither, Chain);
/**
 * Less strict version of [`filterOrElse`](#filterorelse).
 *
 * @category combinators
 * @since 2.9.0
 */

export var filterOrElseW = filterOrElse;
/**
 * Returns a `Right` if is a `Left` (and vice versa).
 *
 * @category combinators
 * @since 2.0.0
 */

export var swap = function swap(ma) {
  return isLeft(ma) ? right(ma.left) : left(ma.right);
};
/**
 * Less strict version of [`orElse`](#orelse).
 *
 * @category combinators
 * @since 2.10.0
 */

export var orElseW = function orElseW(onLeft) {
  return function (ma) {
    return isLeft(ma) ? onLeft(ma.left) : ma;
  };
};
/**
 * Useful for recovering from errors.
 *
 * @category combinators
 * @since 2.0.0
 */

export var orElse = orElseW; // -------------------------------------------------------------------------------------
// interop
// -------------------------------------------------------------------------------------

/**
 * Takes a default and a nullable value, if the value is not nully, turn it into a `Right`, if the value is nully use
 * the provided default as a `Left`.
 *
 * @example
 * import { fromNullable, left, right } from 'fp-ts/Either'
 *
 * const parse = fromNullable('nully')
 *
 * assert.deepStrictEqual(parse(1), right(1))
 * assert.deepStrictEqual(parse(null), left('nully'))
 *
 * @category interop
 * @since 2.0.0
 */

export var fromNullable = function fromNullable(e) {
  return function (a) {
    return a == null ? left(e) : right(a);
  };
};
/**
 * Constructs a new `Either` from a function that might throw.
 *
 * See also [`tryCatchK`](#trycatchk).
 *
 * @example
 * import * as E from 'fp-ts/Either'
 *
 * const unsafeHead = <A>(as: ReadonlyArray<A>): A => {
 *   if (as.length > 0) {
 *     return as[0]
 *   } else {
 *     throw new Error('empty array')
 *   }
 * }
 *
 * const head = <A>(as: ReadonlyArray<A>): E.Either<Error, A> =>
 *   E.tryCatch(() => unsafeHead(as), e => (e instanceof Error ? e : new Error('unknown error')))
 *
 * assert.deepStrictEqual(head([]), E.left(new Error('empty array')))
 * assert.deepStrictEqual(head([1, 2, 3]), E.right(1))
 *
 * @category interop
 * @since 2.0.0
 */

export var tryCatch = function tryCatch(f, onThrow) {
  try {
    return right(f());
  } catch (e) {
    return left(onThrow(e));
  }
};
/**
 * Converts a function that may throw to one returning a `Either`.
 *
 * @category interop
 * @since 2.10.0
 */

export var tryCatchK = function tryCatchK(f, onThrow) {
  return function () {
    var a = [];

    for (var _i = 0; _i < arguments.length; _i++) {
      a[_i] = arguments[_i];
    }

    return tryCatch(function () {
      return f.apply(void 0, a);
    }, onThrow);
  };
};
/**
 * @category interop
 * @since 2.9.0
 */

export var fromNullableK = function fromNullableK(e) {
  var from = fromNullable(e);
  return function (f) {
    return flow(f, from);
  };
};
/**
 * @category interop
 * @since 2.9.0
 */

export var chainNullableK = function chainNullableK(e) {
  var from = fromNullableK(e);
  return function (f) {
    return chain(from(f));
  };
};
/**
 * @category interop
 * @since 2.10.0
 */

export var toUnion = /*#__PURE__*/foldW(identity, identity); // -------------------------------------------------------------------------------------
// utils
// -------------------------------------------------------------------------------------

/**
 * Default value for the `onError` argument of `tryCatch`
 *
 * @since 2.0.0
 */

export function toError(e) {
  return e instanceof Error ? e : new Error(String(e));
}
export function elem(E) {
  return function (a, ma) {
    if (ma === undefined) {
      var elemE_1 = elem(E);
      return function (ma) {
        return elemE_1(a, ma);
      };
    }

    return isLeft(ma) ? false : E.equals(a, ma.right);
  };
}
/**
 * Returns `false` if `Left` or returns the result of the application of the given predicate to the `Right` value.
 *
 * @example
 * import { exists, left, right } from 'fp-ts/Either'
 *
 * const gt2 = exists((n: number) => n > 2)
 *
 * assert.strictEqual(gt2(left('a')), false)
 * assert.strictEqual(gt2(right(1)), false)
 * assert.strictEqual(gt2(right(3)), true)
 *
 * @since 2.0.0
 */

export var exists = function exists(predicate) {
  return function (ma) {
    return isLeft(ma) ? false : predicate(ma.right);
  };
}; // -------------------------------------------------------------------------------------
// do notation
// -------------------------------------------------------------------------------------

/**
 * @since 2.9.0
 */

export var Do = /*#__PURE__*/of(_.emptyRecord);
/**
 * @since 2.8.0
 */

export var bindTo = /*#__PURE__*/bindTo_(Functor);
/**
 * @since 2.8.0
 */

export var bind = /*#__PURE__*/bind_(Chain);
/**
 * @since 2.8.0
 */

export var bindW = bind; // -------------------------------------------------------------------------------------
// pipeable sequence S
// -------------------------------------------------------------------------------------

/**
 * @since 2.8.0
 */

export var apS = /*#__PURE__*/apS_(Apply);
/**
 * @since 2.8.0
 */

export var apSW = apS; // -------------------------------------------------------------------------------------
// sequence T
// -------------------------------------------------------------------------------------

/**
 * @since 2.11.0
 */

export var ApT = /*#__PURE__*/of(_.emptyReadonlyArray); // -------------------------------------------------------------------------------------
// array utils
// -------------------------------------------------------------------------------------

/**
 * Equivalent to `ReadonlyNonEmptyArray#traverseWithIndex(Applicative)`.
 *
 * @since 2.11.0
 */

export var traverseReadonlyNonEmptyArrayWithIndex = function traverseReadonlyNonEmptyArrayWithIndex(f) {
  return function (as) {
    var e = f(0, _.head(as));

    if (isLeft(e)) {
      return e;
    }

    var out = [e.right];

    for (var i = 1; i < as.length; i++) {
      var e_1 = f(i, as[i]);

      if (isLeft(e_1)) {
        return e_1;
      }

      out.push(e_1.right);
    }

    return right(out);
  };
};
/**
 * Equivalent to `ReadonlyArray#traverseWithIndex(Applicative)`.
 *
 * @since 2.11.0
 */

export var traverseReadonlyArrayWithIndex = function traverseReadonlyArrayWithIndex(f) {
  var g = traverseReadonlyNonEmptyArrayWithIndex(f);
  return function (as) {
    return _.isNonEmpty(as) ? g(as) : ApT;
  };
};
/**
 * @since 2.9.0
 */

export var traverseArrayWithIndex = traverseReadonlyArrayWithIndex;
/**
 * @since 2.9.0
 */

export var traverseArray = function traverseArray(f) {
  return traverseReadonlyArrayWithIndex(function (_, a) {
    return f(a);
  });
};
/**
 * @since 2.9.0
 */

export var sequenceArray = /*#__PURE__*/traverseArray(identity);
/**
 * Use [`parse`](./Json.ts.html#parse) instead.
 *
 * @category constructors
 * @since 2.0.0
 * @deprecated
 */

export function parseJSON(s, onError) {
  return tryCatch(function () {
    return JSON.parse(s);
  }, onError);
}
/**
 * Use [`stringify`](./Json.ts.html#stringify) instead.
 *
 * @category constructors
 * @since 2.0.0
 * @deprecated
 */

export var stringifyJSON = function stringifyJSON(u, onError) {
  return tryCatch(function () {
    var s = JSON.stringify(u);

    if (typeof s !== 'string') {
      throw new Error('Converting unsupported structure to JSON');
    }

    return s;
  }, onError);
};
/**
 * Use small, specific instances instead.
 *
 * @category instances
 * @since 2.0.0
 * @deprecated
 */

export var either = {
  URI: URI,
  map: _map,
  of: of,
  ap: _ap,
  chain: _chain,
  reduce: _reduce,
  foldMap: _foldMap,
  reduceRight: _reduceRight,
  traverse: _traverse,
  sequence: sequence,
  bimap: _bimap,
  mapLeft: _mapLeft,
  alt: _alt,
  extend: _extend,
  chainRec: _chainRec,
  throwError: throwError
};
/**
 * Use [`getApplySemigroup`](./Apply.ts.html#getapplysemigroup) instead.
 *
 * Semigroup returning the left-most `Left` value. If both operands are `Right`s then the inner values
 * are concatenated using the provided `Semigroup`
 *
 * @category instances
 * @since 2.0.0
 * @deprecated
 */

export var getApplySemigroup = /*#__PURE__*/getApplySemigroup_(Apply);
/**
 * Use [`getApplicativeMonoid`](./Applicative.ts.html#getapplicativemonoid) instead.
 *
 * @category instances
 * @since 2.0.0
 * @deprecated
 */

export var getApplyMonoid = /*#__PURE__*/getApplicativeMonoid(Applicative);
/**
 * Use [`getApplySemigroup`](./Apply.ts.html#getapplysemigroup) instead.
 *
 * @category instances
 * @since 2.0.0
 * @deprecated
 */

export var getValidationSemigroup = function getValidationSemigroup(SE, SA) {
  return getApplySemigroup_(getApplicativeValidation(SE))(SA);
};
/**
 * Use [`getApplicativeMonoid`](./Applicative.ts.html#getapplicativemonoid) instead.
 *
 * @category instances
 * @since 2.0.0
 * @deprecated
 */

export var getValidationMonoid = function getValidationMonoid(SE, MA) {
  return getApplicativeMonoid(getApplicativeValidation(SE))(MA);
};
/**
 * Use [`getApplicativeValidation`](#getapplicativevalidation) and [`getAltValidation`](#getaltvalidation) instead.
 *
 * @category instances
 * @since 2.0.0
 * @deprecated
 */

export function getValidation(SE) {
  var ap = getApplicativeValidation(SE).ap;
  var alt = getAltValidation(SE).alt;
  return {
    URI: URI,
    _E: undefined,
    map: _map,
    of: of,
    chain: _chain,
    bimap: _bimap,
    mapLeft: _mapLeft,
    reduce: _reduce,
    foldMap: _foldMap,
    reduceRight: _reduceRight,
    extend: _extend,
    traverse: _traverse,
    sequence: sequence,
    chainRec: _chainRec,
    throwError: throwError,
    ap: ap,
    alt: alt
  };
}